import {
  retrieveMassTortEventsV1,
  retrieveMassTortNotesV1,
  retrieveMassTortTasksV1,
  type RetrieveMassTortCollectionParams,
} from "@/lib/data/requests/mass-torts/mass-tort-collections";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIResponse } from "@/lib/types";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

export const retrieveMassTortNotesQueryOptions = (
  data: RetrieveMassTortCollectionParams,
) =>
  queryOptions({
    queryKey: [
      queryKeys.massTorts,
      data.massTortId,
      "notes",
      data.pageNumber ?? 1,
      data.pageSize ?? 50,
    ],
    enabled: Boolean(data.massTortId),
    queryFn: ({ signal }) => retrieveMassTortNotesV1(data, signal),
  });

const getNextCollectionPage = (
  lastPage: APIResponse<unknown[]>,
  allPages: APIResponse<unknown[]>[],
  fallbackPageSize: number,
) => {
  const pageNumber = lastPage.pagination?.pageNumber ?? allPages.length;
  const pageSize = lastPage.pagination?.pageSize ?? fallbackPageSize;
  const count = lastPage.pagination?.count ?? 0;
  return pageNumber * pageSize < count ? pageNumber + 1 : undefined;
};

export const retrieveMassTortNotesInfiniteQueryOptions = (
  data: Omit<RetrieveMassTortCollectionParams, "pageNumber">,
) => {
  const params = { ...data, pageSize: data.pageSize ?? 50 };

  return infiniteQueryOptions({
    queryKey: [
      queryKeys.massTorts,
      data.massTortId,
      "notes",
      "infinite",
      params,
    ],
    enabled: Boolean(data.massTortId),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      retrieveMassTortNotesV1({ ...params, pageNumber: pageParam }, signal),
    getNextPageParam: (lastPage, allPages) =>
      getNextCollectionPage(lastPage, allPages, params.pageSize),
  });
};

export const retrieveMassTortTasksInfiniteQueryOptions = (
  data: Omit<RetrieveMassTortCollectionParams, "pageNumber">,
) => {
  const params = { ...data, pageSize: data.pageSize ?? 50 };

  return infiniteQueryOptions({
    queryKey: [
      queryKeys.massTorts,
      data.massTortId,
      "tasks",
      "infinite",
      params,
    ],
    enabled: Boolean(data.massTortId),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      retrieveMassTortTasksV1({ ...params, pageNumber: pageParam }, signal),
    getNextPageParam: (lastPage, allPages) =>
      getNextCollectionPage(lastPage, allPages, params.pageSize),
  });
};

export const retrieveMassTortEventsInfiniteQueryOptions = (
  data: Omit<RetrieveMassTortCollectionParams, "pageNumber">,
) => {
  const params = { ...data, pageSize: data.pageSize ?? 50 };

  return infiniteQueryOptions({
    queryKey: [
      queryKeys.massTorts,
      data.massTortId,
      "events",
      "infinite",
      params,
    ],
    enabled: Boolean(data.massTortId),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      retrieveMassTortEventsV1({ ...params, pageNumber: pageParam }, signal),
    getNextPageParam: (lastPage, allPages) =>
      getNextCollectionPage(lastPage, allPages, params.pageSize),
  });
};
